<template>
	<div class="recipient-picker" :data-test-id="testId">
		<v-autocomplete
			:model-value="autocompleteValue"
			:search="searchTerm"
			v-bind="searchFieldProps(searchTerm)"
			:items="dropdownItems"
			item-title="displayValue"
			item-value="value"
			:label="label"
			:placeholder="placeholder"
			:loading="showLoading ? 'primary' : false"
			:menu="menuOpen"
			:menu-props="recipientMenuProps"
			multiple
			return-object
			auto-select-first
			chips
			closable-chips
			hide-no-data
			no-filter
			:disabled="disabled"
			close-icon="icon-close-medium"
			@update:model-value="onAutocompleteSelect"
			@update:search="onSearchUpdate"
			@update:menu="menuOpen = $event"
			@keydown="onKeydown"
			@blur="onBlur"
			@paste="onPaste">
			<template #chip="{ item, props: chipProps }">
				<v-chip
					v-bind="chipProps"
					:color="item.raw.valid === false ? 'error' : undefined"
					:prepend-icon="item.raw.valid === false ? 'mdi-alert-circle-outline' : undefined"
					:data-test-id="testId ? `${testId}-chip` : undefined">
					{{ item.raw.displayValue }}
				</v-chip>
			</template>
			<template #item="{ props: itemProps, item }">
				<v-list-item v-bind="itemProps" :title="item.raw.contactName || item.raw.displayValue">
					<v-list-item-subtitle v-if="item.raw.source === 'contact'">
						{{ channel === "email" ? item.raw.value : formatPhoneForDisplay(item.raw.value) }}
					</v-list-item-subtitle>
				</v-list-item>
			</template>
			<template #append-item>
				<v-list-item
					v-if="showAddRow"
					v-test="'recipient-picker-add-row'"
					:title="`Add &quot;${searchTerm}&quot; as a recipient`"
					prepend-icon="mdi-plus"
					@click="commitFromInput(searchTerm)" />
			</template>
			<template #no-data>
				<v-list-item v-if="noDataHint" class="text-medium-emphasis">
					<v-list-item-title>{{ noDataHint }}</v-list-item-title>
				</v-list-item>
			</template>
		</v-autocomplete>
		<div class="d-flex justify-space-between align-center ga-2 mt-1 text-caption">
			<span v-if="errorMessage" v-test="'recipient-picker-error'" class="text-error">{{ errorMessage }}</span>
			<span v-else-if="atLimitNotice" class="text-medium-emphasis">{{ atLimitNotice }}</span>
			<span v-else class="text-medium-emphasis">{{ helperText }}</span>
			<span class="flex-shrink-0" :class="atLimit ? 'text-primary font-weight-bold' : 'text-medium-emphasis'">
				{{ selectedCount }} / {{ maxRecipients }}
			</span>
		</div>
	</div>
</template>

<script lang="ts" setup>
	import { searchFieldProps, isSearchTooLong as exceedsSearchLimit } from "@/helpers/searchLimit";
	import { computed, onBeforeUnmount, ref, toRef } from "vue";
	import { parsePhoneNumberWithError, type CountryCode } from "libphonenumber-js";
	import {
		useContactRecipientSearch,
		formatPhoneForDisplay,
		type ContactRecipient,
		type RecipientChannel
	} from "@/composables/useContactRecipientSearch";
	import { useLatestRequest } from "@/composables/useLatestRequest";
	import { MIN_DIRECTORY_SEARCH_LENGTH } from "@/helpers/directoryQuery";
	import { VuetifyValidation } from "@/helpers/Validation";
	import { debounce } from "@/helpers/utilities";
	import { logError } from "@/helpers/logError";
	import { menuProps as globalMenuProps } from "@/plugins/vuetify";
	import api from "@/api";

	// Tag this picker's dropdown so the scoped CSS below can lift it above the Share dialog. Keep the
	// app-wide menu defaults (maxWidth / mobile viewport margin) — we only ADD the class. The class
	// lands on the teleported `.v-overlay` root, where Vuetify applies the stack z-index. See the
	// <style> block for why this is needed (LSP-16627 — dropdown hid behind the nested Share dialog).
	const recipientMenuProps = { ...globalMenuProps, class: "recipient-picker-menu" };

	/**
	 * LSP-20700 — this picker's own directory-search tuning, deliberately NOT a mirror of
	 * ShareActionDialog's DIRECTORY_SEARCH_DEBOUNCE_MS / DIRECTORY_SEARCH_PAGE_SIZE.
	 *
	 * That dialog's numbers size a fan-out over TWO endpoints (`api.searchCustomers` +
	 * `api.getUsers`) whose rows share one dropdown, which is why its page size had to be equal on
	 * both halves. This is one endpoint, and its results are merged into a roster the picker already
	 * holds — so the only question here is "how many extra contacts are worth fetching per query",
	 * and it has its own answer. `MIN_DIRECTORY_SEARCH_LENGTH` IS shared, because "when does a
	 * directory search happen at all" is one notion across the app (see helpers/directoryQuery.ts).
	 */
	const DIRECTORY_SEARCH_DEBOUNCE_MS = 250;
	const DIRECTORY_SEARCH_PAGE_SIZE = 20;

	/** Only the fields the merge and the local filter actually read. */
	interface CustomerRow {
		id?: string;
		EmailAddresses?: Array<{ id?: string; emailAddress?: string }>;
		PhoneNumbers?: Array<{ id?: string; phoneNumber?: string; phonenumberraw?: string | null }>;
	}

	/**
	 * Union two association arrays, keyed on the identifier itself rather than the row id.
	 *
	 * The row id would look like the obvious key, but the two sides are separate reads of the same
	 * table and a truncated roster row is exactly the case where one side has no row at all to carry
	 * an id. Keying on the value also collapses the same address arriving under different ids.
	 */
	function unionAssociations<T>(primary: T[] | undefined, secondary: T[] | undefined, keyOf: (row: T) => string): T[] {
		const out: T[] = [];
		const seen = new Set<string>();
		for (const row of [...(primary ?? []), ...(secondary ?? [])]) {
			if (!row) continue;
			const key = keyOf(row);
			// A row with no usable key can't be deduped, so keep it rather than drop it.
			if (key && seen.has(key)) continue;
			if (key) seen.add(key);
			out.push(row);
		}
		return out;
	}

	function unionCustomerRecords(rosterRow: CustomerRow, serverRow: CustomerRow): CustomerRow {
		return {
			// Roster scalars win — same contact, and the roster read is not predicate-filtered.
			...serverRow,
			...rosterRow,
			EmailAddresses: unionAssociations(rosterRow.EmailAddresses, serverRow.EmailAddresses, (row) =>
				(row?.emailAddress ?? "").trim().toLowerCase()
			),
			PhoneNumbers: unionAssociations(rosterRow.PhoneNumbers, serverRow.PhoneNumbers, (row) =>
				(row?.phonenumberraw ?? row?.phoneNumber ?? "").replace(/\D/g, "")
			)
		};
	}

	interface Props {
		channel: RecipientChannel;
		modelValue: ContactRecipient[];
		label: string;
		placeholder: string;
		helperText: string;
		customers?: unknown[];
		loading?: boolean;
		maxRecipients?: number;
		disabled?: boolean;
		defaultCountry?: CountryCode;
		testId?: string;
	}

	const props = withDefaults(defineProps<Props>(), {
		customers: () => [],
		loading: false,
		maxRecipients: 20,
		disabled: false
		// defaultCountry intentionally has no default — when undefined, canonicalize requires a
		// leading `+` for phone input. This avoids silently applying "US" during the brief window
		// before the caller's org data has loaded, which would wrong-canonicalize numbers for
		// non-US orgs.
	});

	const emit = defineEmits<{
		(event: "update:modelValue", value: ContactRecipient[]): void;
	}>();

	const emailRule = VuetifyValidation.EmailValidation("Not a valid email address.");

	const searchTerm = ref("");
	const menuOpen = ref(false);

	const selectedValues = computed(() => new Set(props.modelValue.map((r) => r.value)));
	const customersRef = toRef(props, "customers") as ReturnType<typeof toRef<unknown[]>>;

	/**
	 * Contacts found by the server-side directory search for the CURRENT query (LSP-20700).
	 *
	 * The `customers` prop is a snapshot the parent dialog preloads once when it opens
	 * (`Dialogs.getCustomers("", 9999)`), and that snapshot is not guaranteed to be the whole org:
	 * `/customers` applies its LIMIT to the JOINED row set (every include is `duplicating: false`,
	 * so Sequelize builds no subquery), so a contact carrying more than one email/phone/address
	 * consumes more than one row of the page. The loader reads the resulting short page as "that
	 * was the last page" and stops, and the roster is ordered `lastname ASC NULLS LAST` with blanks
	 * last — so the contacts that fall off the end are exactly the ones created in a hurry with no
	 * surname. Result: a contact that project share finds instantly (it queries the server on every
	 * keystroke, via ShareInviteField) was unreachable here, and the user had to retype the address
	 * by hand.
	 *
	 * Rather than making the snapshot bigger — a larger preload is the same bug one org-growth away
	 * — every query the user actually types is also asked of the server, and the answer is merged
	 * into the list the local filter runs over. The roster keeps the dropdown instant and populated
	 * before anything is typed; the server keeps it CORRECT.
	 */
	const directoryCustomers = ref<unknown[]>([]);
	const directorySearching = ref(false);
	const directoryScope = useLatestRequest();

	/**
	 * Server hits first, and a contact in BOTH lists is the UNION of the two records.
	 *
	 * Those are two separate decisions and both matter:
	 *
	 * ORDER — the local filter walks this list and stops at its maxResults cap, so a contact the
	 * snapshot is missing must not queue up behind 50 roster matches for the same term; that would
	 * reproduce the bug for broad queries.
	 *
	 * CONTENT — neither record is reliably complete, so choosing either one drops identifiers:
	 *
	 *   * The SERVER row carries only the associations that matched the server's own predicate.
	 *     `/customers` filters on `$EmailAddresses.emailAddress$` / `$PhoneNumbers.phonenumberraw$`
	 *     through `required:false, duplicating:false` joins, so the condition filters the JOINED rows
	 *     and the returned arrays hold the matching subset. Searching `bob@ex.com` returns Bob with
	 *     that one address, so taking the server row wholesale deletes `personal@me.com` from the
	 *     dropdown for that query — which the local filter, matching a contact by any identifier,
	 *     would otherwise have offered.
	 *   * The ROSTER row is a slice of the very JOIN+LIMIT snapshot this ticket is about. The page
	 *     limit counts joined rows, so it can fall in the MIDDLE of a contact — the last contact on a
	 *     short page keeps only the identifiers that fit before the cut, which can be everything
	 *     EXCEPT the address the user just typed. Taking the roster row wholesale then discards the
	 *     identifier that is the whole reason the server returned that contact.
	 *
	 * Both failures are the same dead end this ticket is about, reached from opposite directions, so
	 * the merge takes the union: roster scalars (the fuller record) with both sides' emails and
	 * phones deduped together. A contact only one side knows about is used as-is.
	 */
	const mergedCustomers = computed<unknown[]>(() => {
		const roster = (customersRef.value ?? []) as CustomerRow[];
		const found = directoryCustomers.value as CustomerRow[];
		if (found.length === 0) return roster;

		const rosterById = new Map<string, CustomerRow>();
		for (const customer of roster) {
			if (customer?.id) rosterById.set(customer.id, customer);
		}

		const merged: CustomerRow[] = [];
		const takenIds = new Set<string>();
		for (const customer of found) {
			const id = customer?.id;
			if (!id) {
				merged.push(customer);
				continue;
			}
			if (takenIds.has(id)) continue;
			takenIds.add(id);
			const rosterRow = rosterById.get(id);
			merged.push(rosterRow ? unionCustomerRecords(rosterRow, customer) : customer);
		}
		for (const customer of roster) {
			if (customer?.id && takenIds.has(customer.id)) continue;
			merged.push(customer);
		}
		return merged;
	});

	// Pass defaultCountry as a reactive toRef so the composable re-expands contact phones when
	// the caller's org data resolves (undefined → "GB", say). Passing the plain value captures
	// it at call time and misses later updates — a bug BugBot caught.
	const defaultCountryRef = toRef(props, "defaultCountry");

	const { search, results } = useContactRecipientSearch({
		channel: props.channel,
		customers: mergedCustomers,
		selectedValues,
		defaultCountry: defaultCountryRef
	});

	/**
	 * Teardown latch.
	 *
	 * `debouncedDirectorySearch.cancel()` below drops a timer that is already queued, but nothing
	 * stops a NEW one being armed after it: Vue runs a component's own beforeUnmount hooks BEFORE
	 * tearing its children down, so an `update:search` that VAutocomplete emits while unmounting
	 * still reaches our handler — after the cancel, and after useLatestRequest's abortCurrent has
	 * already run. A timer armed at that point has no owner left to cancel it and would reach
	 * `api.searchCustomers` against a dead component, which is the hazard
	 * `helpers/utilities.debounce`'s cancel() exists to prevent in the first place.
	 *
	 * So the gap this closes is the RE-ARM, not the cancel. Checked in both places rather than one:
	 * queueDirectorySearch stops a timer being armed, runDirectorySearch stops one that was already
	 * in flight from starting a request.
	 */
	let isTornDown = false;

	async function runDirectorySearch(rawTerm: string): Promise<void> {
		if (isTornDown) return;
		const trimmed = rawTerm.trim();
		// Re-checked at FIRE time, not just when the search was queued: the term can fall back below
		// the minimum (or be cleared entirely) while this call sits in the debounce.
		if (trimmed.length < MIN_DIRECTORY_SEARCH_LENGTH || exceedsSearchLimit(rawTerm)) return;
		// startRequest aborts the previous attempt, so fast typing never lets an older response
		// overwrite a newer one.
		const request = directoryScope.startRequest();
		directorySearching.value = true;
		try {
			const result = await api.searchCustomers(trimmed, { signal: request.signal }, DIRECTORY_SEARCH_PAGE_SIZE);
			if (!directoryScope.isCurrent(request.requestId)) return;
			directoryCustomers.value = (result?.rows ?? []) as unknown[];
		} catch (error) {
			// A superseded/aborted request is a newer keystroke, not a failure — logging it would
			// make normal typing look like an outage.
			if (request.signal.aborted || directoryScope.isSuperseded(request.requestId)) return;
			// Swallowed deliberately: the roster results are still on screen and the user can still
			// type the address by hand, so a failed lookup must narrow the dropdown, not break the
			// dialog. It is logged so a systematically failing search is still visible in Datadog.
			logError("RecipientPicker: contact directory search failed", error, {
				channel: props.channel,
				queryLength: trimmed.length
			});
		} finally {
			// Only the newest request owns the flag — an older one settling after being superseded
			// must not clear the spinner the newer one turned on.
			if (!directoryScope.isSuperseded(request.requestId)) directorySearching.value = false;
		}
	}

	const debouncedDirectorySearch = debounce((term: string) => {
		void runDirectorySearch(term);
	}, DIRECTORY_SEARCH_DEBOUNCE_MS);

	/**
	 * The term the server has already been asked about (or is being asked about right now).
	 *
	 * VAutocomplete echoes `update:search` back after the bound `:search` value updates, so every
	 * keystroke reaches queueDirectorySearch TWICE. Without this guard each one costs two identical
	 * requests, and the echo lands late enough that the debounce does not coalesce them.
	 *
	 * Deliberately NOT cleared when a search fails: the retry that buys is one identical request
	 * fired immediately by the echo, which is the least likely moment for it to succeed. Editing the
	 * query at all (including backspacing to a term and back) gives a different term, and so a retry.
	 */
	let requestedDirectoryTerm: string | null = null;

	function queueDirectorySearch(rawTerm: string): void {
		if (isTornDown) return;
		const trimmed = rawTerm.trim();
		if (trimmed.length < MIN_DIRECTORY_SEARCH_LENGTH || exceedsSearchLimit(rawTerm)) {
			// Below the minimum the dropdown is the roster again. Drop the queued search, the one in
			// flight, AND the previous query's hits — leaving those merged in would pin contacts
			// found for an earlier term to the top of an unrelated list.
			debouncedDirectorySearch.cancel();
			directoryScope.abortCurrent();
			directorySearching.value = false;
			directoryCustomers.value = [];
			requestedDirectoryTerm = null;
			return;
		}
		if (trimmed === requestedDirectoryTerm) return;
		requestedDirectoryTerm = trimmed;
		debouncedDirectorySearch(trimmed);
	}

	onBeforeUnmount(() => {
		// Latch FIRST, then cancel: the latch is what keeps a late child emission from re-arming the
		// timer we are about to drop. useLatestRequest aborts the in-flight request for us.
		isTornDown = true;
		debouncedDirectorySearch.cancel();
	});

	// Prime the dropdown so opening the menu without typing shows the full customer list.
	search("");

	// autocompleteValue is a read-only mirror of modelValue for the v-autocomplete's multi-select display.
	const autocompleteValue = computed(() => props.modelValue);
	const dropdownItems = computed<ContactRecipient[]>(() => results.value);

	const atLimit = computed(() => props.modelValue.length >= props.maxRecipients);
	const selectedCount = computed(() => props.modelValue.length);
	// One progress bar for both sources: the parent's roster load on open, and the per-query
	// directory search. `hide-no-data` means an empty dropdown renders nothing at all, so this bar
	// is the only signal the user gets that a query is still being answered.
	const showLoading = computed(() => props.loading || directorySearching.value);

	const canonicalInput = computed(() => canonicalize(searchTerm.value));
	const inputIsValid = computed(() => canonicalInput.value !== null);
	const inputDuplicatesSelection = computed(() => canonicalInput.value !== null && selectedValues.value.has(canonicalInput.value));
	const inputDuplicatesDropdown = computed(
		() => canonicalInput.value !== null && dropdownItems.value.some((item) => item.value === canonicalInput.value)
	);

	const showAddRow = computed(() => {
		if (atLimit.value) return false;
		const term = searchTerm.value.trim();
		if (!term) return false;
		if (!inputIsValid.value) return false;
		if (inputDuplicatesSelection.value) return false;
		if (inputDuplicatesDropdown.value) return false;
		return true;
	});

	const noDataHint = computed<string>(() => {
		if (atLimit.value) return `Recipient limit reached (${props.maxRecipients} max).`;
		const term = searchTerm.value.trim();
		if (!term) return "Type to search contacts.";
		// Duplicate of an already-added chip — tell the user why nothing happens on Enter instead
		// of leaving the dropdown silently empty.
		if (inputDuplicatesSelection.value) return "Already added.";
		if (dropdownItems.value.length > 0) return "";
		// An unanswered directory search must not be reported as "no such contact" (LSP-20700) —
		// that is the message that sent users off to retype the address by hand.
		if (directorySearching.value) return "Searching contacts…";
		if (inputIsValid.value) return "";
		if (props.channel === "email") return "Not a valid email address.";
		return "This doesn't look like a valid phone number.";
	});

	const invalidCount = computed(() => props.modelValue.filter((recipient) => recipient.valid === false).length);

	// Per-channel inline error summarising the invalid pills, shown below the field. We intentionally
	// do NOT redden the whole input box — validation is per pill/recipient (LSP-16629), so the red
	// chips carry the per-entry signal and this message just tells the user one or more need fixing.
	const errorMessage = computed<string>(() => {
		if (invalidCount.value === 0) return "";
		const noun = props.channel === "email" ? "email address" : "phone number";
		if (invalidCount.value === 1) return `One ${noun} isn't valid. Fix or remove the highlighted entry.`;
		const plural = props.channel === "email" ? "email addresses" : "phone numbers";
		return `${invalidCount.value} ${plural} aren't valid. Fix or remove the highlighted entries.`;
	});

	// At the cap, commitFromInput can't accept new input. Surface a persistent notice so a value
	// typed at the limit (then blurred or sent) is explained rather than silently ignored — the
	// dropdown's "limit reached" hint isn't visible on blur/Send. (LSP-16630 anti-silent-drop.)
	const atLimitNotice = computed(() =>
		atLimit.value ? `You've added the maximum of ${props.maxRecipients} recipients. Remove one to add another.` : ""
	);

	function canonicalize(raw: string): string | null {
		const trimmed = raw.trim();
		if (!trimmed) return null;
		if (props.channel === "email") {
			const result = emailRule.isValid(trimmed);
			if (result !== true) return null;
			return trimmed.toLowerCase();
		}
		// When defaultCountry is set (org loaded), users can skip the country code and we'll
		// infer it. When it's undefined (org not yet loaded), libphonenumber-js requires the
		// caller to include + — safer than silently guessing.
		try {
			const parsed = props.defaultCountry
				? parsePhoneNumberWithError(trimmed, { defaultCountry: props.defaultCountry, extract: false })
				: parsePhoneNumberWithError(trimmed, { extract: false });
			if (!parsed.isValid()) return null;
			return parsed.number;
		} catch {
			return null;
		}
	}

	function buildManualRecipient(canonical: string): ContactRecipient {
		if (props.channel === "email") {
			return {
				channel: "email",
				value: canonical,
				displayValue: canonical,
				source: "manual",
				valid: true
			};
		}
		return {
			channel: "phone",
			value: canonical,
			displayValue: formatPhoneForDisplay(canonical),
			source: "manual",
			valid: true
		};
	}

	// An entry the user typed that failed validation. Preserved as a red pill (LSP-16629) keyed on
	// its raw text, so it's visible and fixable rather than silently dropped. `value` holds the raw
	// text (there's no canonical form for invalid input); the parent blocks send while any exist.
	function buildInvalidRecipient(rawTrimmed: string): ContactRecipient {
		return {
			channel: props.channel,
			value: rawTrimmed,
			displayValue: rawTrimmed,
			source: "manual",
			valid: false
		};
	}

	function commitRecipients(next: ContactRecipient[]): void {
		emit("update:modelValue", next);
	}

	// Clear the typed input AND re-sync the composable's internal search term. The composable keeps
	// its own searchTerm and only learns of changes through search(); setting our local searchTerm
	// to "" without this leaves the composable filtering the dropdown by the stale prior query, so
	// after adding then removing a contact the dropdown stays narrowed to just that contact instead
	// of resetting to the full list (LSP-16678 / LSP-16679). Always reset both together.
	function clearSearchTerm(): void {
		searchTerm.value = "";
		search("");
		// Same reset for the server half, for the same reason: hits found for the term just
		// committed must not stay merged into the cleared dropdown.
		queueDirectorySearch("");
	}

	function commitFromInput(raw: string): void {
		if (atLimit.value) return;
		const trimmed = raw.trim();
		if (!trimmed) return;
		const canonical = canonicalize(raw);
		if (canonical === null) {
			// Invalid input is preserved as a red pill rather than discarded (LSP-16629). Dedupe by
			// raw text so repeated Enter on the same value doesn't stack identical invalid chips.
			const alreadyPresent = props.modelValue.some((recipient) => recipient.valid === false && recipient.value === trimmed);
			if (!alreadyPresent) commitRecipients([...props.modelValue, buildInvalidRecipient(trimmed)]);
			clearSearchTerm();
			return;
		}
		// Valid duplicate: leave the text in place so the "Already added." hint stays visible.
		if (selectedValues.value.has(canonical)) return;
		commitRecipients([...props.modelValue, buildManualRecipient(canonical)]);
		clearSearchTerm();
	}

	// Email addresses never contain whitespace, so splitting on any whitespace (plus
	// punctuation / newlines) is safe — users commonly paste "a@x.com b@y.com" or
	// "a@x.com, b@y.com". Phone numbers DO contain spaces ("+1 212 567 8901"), so for
	// the phone channel we only split on explicit punctuation / newlines.
	function getSeparatorRegex(): RegExp {
		return props.channel === "email" ? /[,;\s]+/ : /[,;\n]+/;
	}

	function hasSeparator(text: string): boolean {
		return getSeparatorRegex().test(text);
	}

	function commitPasted(raw: string): void {
		const fragments = raw
			.split(getSeparatorRegex())
			.map((fragment) => fragment.trim())
			.filter(Boolean);
		if (fragments.length === 0) return;
		let next = props.modelValue.slice();
		const seen = new Set(selectedValues.value);
		for (const fragment of fragments) {
			if (next.length >= props.maxRecipients) break;
			const canonical = canonicalize(fragment);
			if (canonical === null) continue;
			if (seen.has(canonical)) continue;
			seen.add(canonical);
			next = [...next, buildManualRecipient(canonical)];
		}
		if (next.length !== props.modelValue.length) {
			commitRecipients(next);
			clearSearchTerm();
		}
	}

	function onSearchUpdate(value: string | null): void {
		const term = value ?? "";
		searchTerm.value = term;
		search(term);
		// Ask the server the same question, debounced. The local filter answers instantly from the
		// roster; this is what finds a contact the roster snapshot never contained (LSP-20700).
		queueDirectorySearch(term);
		if (term.trim()) menuOpen.value = true;
	}

	function onAutocompleteSelect(values: ContactRecipient[]): void {
		const capped = values.slice(0, props.maxRecipients);
		commitRecipients(capped);
		clearSearchTerm();
	}

	function onKeydown(event: KeyboardEvent): void {
		if (event.key === "Backspace" && !searchTerm.value && props.modelValue.length > 0) {
			event.preventDefault();
			commitRecipients(props.modelValue.slice(0, -1));
			return;
		}
		if (event.key === "," || event.key === ";") {
			event.preventDefault();
			commitFromInput(searchTerm.value);
			return;
		}
		// Space commits for email only — emails can't contain spaces, so pressing space signals
		// "next address please." For phone, spaces are valid formatting and must pass through.
		if (event.key === " " && props.channel === "email") {
			event.preventDefault();
			commitFromInput(searchTerm.value);
			return;
		}
		// Enter and Tab: if the menu is open and contact rows exist, let Vuetify handle the
		// selection via auto-select-first. Otherwise commit the free-text input ourselves.
		if (event.key === "Enter" || event.key === "Tab") {
			// Multi-fragment input (e.g. autofilled or typed-through-separator text like
			// "a@x.com b@y.com c@z.com invalid") commits every valid fragment and drops the
			// rest — same forgiving behaviour as paste. A single invalid fragment should
			// never block a mostly-valid list.
			if (hasSeparator(searchTerm.value)) {
				event.preventDefault();
				commitPasted(searchTerm.value);
				return;
			}
			// Let the dropdown's auto-select-first pick a matching contact when one is showing.
			if (menuOpen.value && dropdownItems.value.length > 0) return;
			// Single free-text entry: commit it as a pill — valid → grey, invalid → red (preserved,
			// not dropped — LSP-16629). Swallow a bare Enter on an empty field so it can't submit a
			// parent form / close the dialog.
			if (searchTerm.value.trim()) {
				event.preventDefault();
				commitFromInput(searchTerm.value);
			} else if (event.key === "Enter") {
				event.preventDefault();
			}
		}
	}

	// Commit whatever is typed when focus leaves the field or the dialog flushes before Send, so a
	// value the user typed but never pressed Enter on is never silently lost (LSP-16630). Valid text
	// becomes a normal pill; invalid text becomes a red pill that stays visible and blocks send
	// (LSP-16629) — commitFromInput is a no-op on empty input and dedupes. We always commit rather
	// than trying to guess whether the text was an "abandoned" partial contact search: silently
	// dropping a typed recipient is the exact failure LSP-16630 is about, and a stray invalid pill
	// is visible and removable in one click.
	function onBlur(event: FocusEvent): void {
		// Read the live input value off the event target: VAutocomplete resets its search on blur,
		// which can clear our reactive searchTerm before this handler runs. Use `||` (not `??`) so an
		// already-cleared DOM value falls back to searchTerm rather than no-opping into a silent drop.
		const live = (event.target as HTMLInputElement | null)?.value;
		commitFromInput(live || searchTerm.value);
	}

	// Imperatively commit any pending typed input. The Share dialog calls this on both pickers
	// before reading recipients on Send, so an un-Entered value is committed deterministically
	// rather than racing the blur that clicking Send triggers (LSP-16630).
	function flushPendingInput(): void {
		commitFromInput(searchTerm.value);
	}

	function onPaste(event: ClipboardEvent): void {
		const text = event.clipboardData?.getData("text") ?? "";
		if (!hasSeparator(text)) return;
		event.preventDefault();
		commitPasted(text);
	}

	defineExpose({
		commitFromInput,
		commitPasted,
		onAutocompleteSelect,
		onSearchUpdate,
		onBlur,
		flushPendingInput,
		noDataHint,
		errorMessage,
		dropdownItems,
		directorySearching
	});
</script>

<style scoped>
	.recipient-picker {
		display: flex;
		flex-direction: column;
	}
</style>

<!--
	NOT scoped: the autocomplete dropdown is teleported to the overlay container, outside this
	component's scoped DOM, so a scoped rule can't reach it.

	Why this is needed (LSP-16627): the shared Dialog.vue force-paints every dialog at z-index 9000
	via an inline style, but Vuetify's overlay stack (useStack) ignores that 9000 when the Share
	dialog is opened NESTED inside the report-viewer <v-dialog> — it derives the dialog's tracked
	z-index from the report dialog (~2410) instead. The dropdown then computes ~2420 and renders
	BEHIND the inline-forced 9000 dialog (only in the nested/report-viewer case; standalone it works).
	Force the dropdown above the 9000 dialog (and below the 10000 toasts). !important is required:
	the value it overrides is Vuetify's own inline stack z-index, which a plain rule can't beat.
-->
<style>
	.v-overlay.recipient-picker-menu {
		z-index: 9100 !important;
	}
</style>
